import { isDirectFlightAvailable, type SearchCriteria, type Selection } from '@/entities/searchCriteria';
import { getBudgetPresets, toggleBudgetPreset } from './budgetFilter';
import { isEmiratesDirection } from './emiratesFilter';
import { AND_FILTER_GROUP_IDS, filterProducts, hasFilteredProducts } from './filterProducts';
import {
  clearFilterGroup,
  createEmptyFiltersModel,
  getFilterGroupOptions,
  isFilterOptionSelected,
  setBudgetFilter,
  toggleFilter,
} from './filtersModel';
import { getDictionaryPrepaymentsFilterKeys, getPrepaymentsFilterOptions } from './prepaymentsFilter';
import { RESORTS_FILTER_GROUP_ID, getResortsFilterOptions } from './resortsFilter';
import { isSearchFilterGroup, isSearchFilterOptionSelected } from './searchFilters';
import { getSerpCardPriceValue, getSerpCardTouristsCount } from './serpCardView';
import { getSerpResultsDictionaries } from './serpResultsDictionaries';
import { SERP_FILTER_GROUPS } from '../config/filters';
import type {
  FilterGroupId,
  FiltersModel,
  SearchCriteriaFilters,
  SerpBudgetFilterGroup,
  SerpBudgetFilterGroupView,
  SerpBudgetPresetView,
  SerpDynamicFilterOptions,
  SerpFilterGroupView,
  SerpFilterOption,
  SerpFilterOptionView,
  SerpOptionsFilterGroup,
  SerpOptionsFilterGroupView,
  SerpProduct,
  SerpCriteriaFilterGroup,
  SerpFilterGroup,
  SerpFilterGroupId,
  SerpFilterViewContext,
  SerpResultsDictionaries,
  SerpResultsOperator,
} from '../model/types';

function getPrices(products: readonly SerpProduct[]): number[] {
  return products.map((product) => getSerpCardPriceValue(product)).filter((price): price is number => price !== null);
}

function getMinPrice(prices: readonly number[]): number {
  return prices.reduce((min, price) => (price < min ? price : min));
}

function getMaxPrice(prices: readonly number[]): number {
  return prices.reduce((max, price) => (price > max ? price : max));
}

function findTouristsCount(products: readonly SerpProduct[]): number {
  for (const product of products) {
    const touristsCount = getSerpCardTouristsCount(product);

    if (touristsCount > 0) return touristsCount;
  }

  return 0;
}

function getGroupFiltersModel(filtersModel: FiltersModel, groupId: FilterGroupId): FiltersModel {
  return AND_FILTER_GROUP_IDS.has(groupId) ? filtersModel : clearFilterGroup(filtersModel, groupId);
}

function getOperatorFilterOption({ id, name, nameRu }: SerpResultsOperator): SerpFilterOption {
  if (!nameRu || nameRu === name) return { key: String(id), label: name };

  return { key: String(id), label: nameRu, secondaryLabel: name };
}

export function getOperatorsFilterOptions(operators: SerpResultsDictionaries['operators']): SerpFilterOption[] {
  return operators.map((operator) => getOperatorFilterOption(operator));
}

export function getDynamicFilterOptions(
  dictionaries: SerpResultsDictionaries,
  destination: Selection | null = null,
  fromCity: SearchCriteria['fromCity'] = null,
): SerpDynamicFilterOptions {
  return {
    prepayments: getPrepaymentsFilterOptions(getDictionaryPrepaymentsFilterKeys(dictionaries.prepayments)),
    operators: getOperatorsFilterOptions(dictionaries.operators),
    resorts: getResortsFilterOptions(destination),
    directFlightAvailable: isDirectFlightAvailable({ fromCity, destination }),
    emiratesAvailable: isEmiratesDirection({ fromCity, destination }),
  };
}

function toOptionView(option: SerpFilterOption, selected: boolean, disabled: boolean): SerpFilterOptionView {
  return {
    key: option.key,
    label: option.label,
    secondaryLabel: option.secondaryLabel,
    description: option.description,
    selected,
    disabled,
  };
}

function toOptionsGroupView(
  group: SerpOptionsFilterGroup | SerpCriteriaFilterGroup,
  options: SerpFilterOptionView[],
): SerpOptionsFilterGroupView {
  return {
    id: group.id,
    control: group.control,
    title: group.title,
    component: group.component,
    resetLabel: group.control === 'radio' ? group.resetLabel : undefined,
    appendIcon: group.appendIcon,
    optionIcon: group.optionIcon,
    optionIconColor: group.optionIconColor,
    tooltip: group.tooltip,
    options,
  };
}

function getSearchGroupView(
  criteriaFilters: SearchCriteriaFilters,
  dynamicOptions: SerpDynamicFilterOptions,
  group: SerpCriteriaFilterGroup,
): SerpOptionsFilterGroupView | null {
  if (group.id === RESORTS_FILTER_GROUP_ID) return toOptionsGroupView(group, []);

  const options: SerpFilterOptionView[] = getFilterGroupOptions(group, dynamicOptions).map((option) =>
    toOptionView(option, isSearchFilterOptionSelected(criteriaFilters, group.id, option.key), false),
  );

  return options.length ? toOptionsGroupView(group, options) : null;
}

function getOptionsGroupView(
  { products, filtersModel, dynamicOptions }: SerpFilterViewContext,
  group: SerpOptionsFilterGroup,
): SerpOptionsFilterGroupView | null {
  const groupProducts = filterProducts(products, getGroupFiltersModel(filtersModel, group.id));
  const options: SerpFilterOptionView[] = getFilterGroupOptions(group, dynamicOptions).map((option) => {
    const selected = isFilterOptionSelected(filtersModel, group, option.key);
    const optionFiltersModel = toggleFilter(createEmptyFiltersModel(), group.id, option.key);

    return toOptionView(option, selected, !selected && !hasFilteredProducts(groupProducts, optionFiltersModel));
  });

  return options.length ? toOptionsGroupView(group, options) : null;
}

function getBudgetPresetView(
  products: readonly SerpProduct[],
  filtersModel: FiltersModel,
  preset: number,
): SerpBudgetPresetView {
  const selected = filtersModel.budget.max === preset;
  const presetFiltersModel = setBudgetFilter(filtersModel, toggleBudgetPreset(filtersModel.budget, preset));

  return {
    value: preset,
    disabled: !selected && !hasFilteredProducts(products, presetFiltersModel),
  };
}

function getBudgetGroupView(
  products: readonly SerpProduct[],
  filtersModel: FiltersModel,
  group: SerpBudgetFilterGroup,
): SerpBudgetFilterGroupView | null {
  const budgetProducts = filterProducts(products, clearFilterGroup(filtersModel, group.id));
  const prices = getPrices(budgetProducts);

  if (!prices.length) return null;

  const allPrices = getPrices(products);

  return {
    id: group.id,
    control: group.control,
    title: group.title,
    component: group.component,
    minPrice: getMinPrice(prices),
    maxPrice: getMaxPrice(prices),
    touristsCount: findTouristsCount(budgetProducts),
    presets: getBudgetPresets(getMinPrice(allPrices), getMaxPrice(allPrices)).map((preset) =>
      getBudgetPresetView(products, filtersModel, preset),
    ),
  };
}

function getGroupView(
  context: SerpFilterViewContext,
  group: SerpOptionsFilterGroup | SerpBudgetFilterGroup,
): SerpFilterGroupView | null {
  if (group.control === 'budget') return getBudgetGroupView(context.products, context.filtersModel, group);

  return getOptionsGroupView(context, group);
}

export function findFilterGroup(groupId: SerpFilterGroupId): SerpFilterGroup | undefined {
  return SERP_FILTER_GROUPS.find((group) => group.id === groupId);
}

export function getFilterGroupView(
  context: SerpFilterViewContext,
  criteriaFilters: SearchCriteriaFilters,
  group: SerpFilterGroup,
): SerpFilterGroupView | null {
  return isSearchFilterGroup(group)
    ? getSearchGroupView(criteriaFilters, context.dynamicOptions, group)
    : getGroupView(context, group);
}

export function getFiltersGroups(
  products: readonly SerpProduct[],
  filtersModel: FiltersModel,
  criteriaFilters: SearchCriteriaFilters,
  dynamicOptions: SerpDynamicFilterOptions = getDynamicFilterOptions(getSerpResultsDictionaries(products)),
): SerpFilterGroupView[] {
  const context: SerpFilterViewContext = { products, filtersModel, dynamicOptions };
  const filtersGroups: SerpFilterGroupView[] = [];
  for (const group of SERP_FILTER_GROUPS) {
    const groupView = getFilterGroupView(context, criteriaFilters, group);

    if (groupView) filtersGroups.push(groupView);
  }

  return filtersGroups;
}

function isSearchGroupAvailable(dynamicOptions: SerpDynamicFilterOptions, group: SerpCriteriaFilterGroup): boolean {
  return group.id === RESORTS_FILTER_GROUP_ID || getFilterGroupOptions(group, dynamicOptions).length > 0;
}

function isBudgetGroupAvailable(
  { products, filtersModel }: SerpFilterViewContext,
  group: SerpBudgetFilterGroup,
): boolean {
  return filterProducts(products, clearFilterGroup(filtersModel, group.id)).some(
    (product) => getSerpCardPriceValue(product) !== null,
  );
}

function isOptionsGroupAvailable(
  { products, filtersModel, dynamicOptions }: SerpFilterViewContext,
  group: SerpOptionsFilterGroup,
): boolean {
  return getFilterGroupOptions(group, dynamicOptions).some(
    (option) =>
      isFilterOptionSelected(filtersModel, group, option.key) ||
      hasFilteredProducts(products, toggleFilter(createEmptyFiltersModel(), group.id, option.key)),
  );
}

export function isFilterGroupAvailable(context: SerpFilterViewContext, group: SerpFilterGroup): boolean {
  if (isSearchFilterGroup(group)) return isSearchGroupAvailable(context.dynamicOptions, group);

  if (group.control === 'budget') return isBudgetGroupAvailable(context, group);

  return isOptionsGroupAvailable(context, group);
}
